import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { BORDER, CATEGORY_COLUMN_WIDTH } from './notificationTokens';

interface NotificationSettingsSkeletonProps {
  roleCount: number;
  channelCount: number;
  categoryCount: number;
}

/**
 * Loading placeholder shaped like the notification matrix. Uses the same column
 * model as NotificationMatrix — a fixed category column plus equal-width role ×
 * channel columns — so the chip placeholders line up with the real matrix that
 * replaces it (rather than clustering beside the label).
 */
const NotificationSettingsSkeleton = ({
  roleCount,
  channelCount,
  categoryCount,
}: NotificationSettingsSkeletonProps) => {
  const roles = Math.max(roleCount, 1);
  const channels = Math.max(channelCount, 1);
  const categories = Math.max(categoryCount, 1);
  const chipColumns = roles * channels;

  // Mirrors the matrix's fixed-layout columns: category column + equal cells.
  const gridTemplateColumns = `${CATEGORY_COLUMN_WIDTH}px repeat(${chipColumns}, minmax(0, 1fr))`;

  const centeredCell = { display: 'flex', justifyContent: 'center' } as const;

  return (
    <Box aria-busy="true" aria-label="Loading notification settings">
      {/* role header placeholders, each spanning its channel columns */}
      <Box sx={{ display: 'grid', gridTemplateColumns, alignItems: 'end', mb: 3 }}>
        <Box />
        {Array.from({ length: roles }).map((_, roleIndex) => (
          <Box key={roleIndex} sx={{ ...centeredCell, gridColumn: `span ${channels}` }}>
            <Skeleton variant="text" width={120} sx={{ fontSize: '1.25rem' }} />
          </Box>
        ))}
      </Box>

      {/* one row per category */}
      {Array.from({ length: categories }).map((_, categoryIndex) => (
        <Box
          key={categoryIndex}
          sx={{
            display: 'grid',
            gridTemplateColumns,
            alignItems: 'center',
            py: 2.25,
            borderBottom: categoryIndex === categories - 1 ? 'none' : `1px solid ${BORDER.subtle}`,
          }}
        >
          <Skeleton variant="text" sx={{ width: 150, fontSize: '1rem' }} />
          {Array.from({ length: chipColumns }).map((__, chipIndex) => (
            <Box key={chipIndex} sx={centeredCell}>
              <Skeleton variant="rounded" width={40} height={40} />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export { NotificationSettingsSkeleton };
